import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './CategoryBar.css';

/* Íconos por defecto (contenido interno del <svg>), tomados del POS. */
const ICONS = {
  todos: '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>',
  desayuno: '<circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/>',
  entradas: '<path d="M3 2v7a3 3 0 003 3v10M5 2v6M7 2v6M14 2c-1.5 1.5-2 4-2 7s.5 4 2 4v9"/>',
  clanes: '<path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>',
  delicias: '<polygon points="12 2 15 9 22 9 16 14 18 21 12 17 6 21 8 14 2 9 9 9"/>',
  bebidas: '<path d="M6 2h12l-1 5H7L6 2zM7 7l1.5 13a2 2 0 002 2h3a2 2 0 002-2L17 7"/>',
  postres: '<path d="M4 13h16v3a4 4 0 01-4 4H8a4 4 0 01-4-4v-3z"/><path d="M12 13V8M9 8a3 3 0 016 0"/>',
  sopas: '<path d="M3 8h18a9 9 0 01-9 9 9 9 0 01-9-9zM12 5V3M9 5V3.5M15 5V3.5"/>',
  combos: '<rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a4 4 0 018 0v2"/>',
  promos: '<path d="M20.6 13.4 11 3.8a2 2 0 00-1.4-.6H4a1 1 0 00-1 1v5.6a2 2 0 00.6 1.4l9.6 9.6a2 2 0 002.8 0l4.6-4.6a2 2 0 000-2.8z"/><circle cx="7.5" cy="7.5" r="1"/>',
  mariscos: '<path d="M12 3c4 0 7 3 7 7 0 5-7 11-7 11S5 15 5 10c0-4 3-7 7-7z"/><path d="M9 10h6"/>',
};
const FALLBACK = '<circle cx="12" cy="12" r="9"/>';

/**
 * CategoryBar — barra de categorías con scroll horizontal y flechas de carrusel.
 *
 * Props:
 * @param {Array<{key:string,name:string,count?:number,icon?:React.ReactNode}>} categories  Categorías (requerido).
 * @param {string} [activeKey]         Categoría activa (modo controlado).
 * @param {string} [defaultActiveKey]  Activa inicial (modo no controlado; por defecto la primera).
 * @param {(key:string)=>void} [onChange]  Se llama al seleccionar una categoría.
 * @param {boolean} [showCount=true]    Mostrar el badge de conteo.
 * @param {number}  [scrollStep=240]    Píxeles que avanza cada flecha.
 * @param {string} [className]
 */
export function CategoryBar({
  categories = [],
  activeKey,
  defaultActiveKey,
  onChange,
  showCount = true,
  scrollStep = 240,
  className = '',
  ...rest
}) {
  const scrollerRef = useRef(null);
  const [internal, setInternal] = useState(defaultActiveKey ?? categories[0]?.key);
  const active = activeKey ?? internal;

  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const overflow = el.scrollWidth - el.clientWidth > 4;
    setCanPrev(overflow && el.scrollLeft > 4);
    setCanNext(overflow && el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useLayoutEffect(() => { updateArrows(); }, [categories, updateArrows]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrows();
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    window.addEventListener('resize', updateArrows);
    return () => { ro.disconnect(); window.removeEventListener('resize', updateArrows); };
  }, [updateArrows]);

  const scrollBy = (dir) => scrollerRef.current?.scrollBy({ left: dir * scrollStep, behavior: 'smooth' });

  const select = (key, e) => {
    if (activeKey === undefined) setInternal(key);
    onChange?.(key);
    e?.currentTarget?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' });
  };

  const onKeyNav = (e, i) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = categories[(i + dir + categories.length) % categories.length];
    if (next) select(next.key);
  };

  return (
    <div className={`sp-catbar ${className}`.trim()} {...rest}>
      <button
        type="button"
        className={`sp-catbar__arrow sp-catbar__arrow--left ${canPrev ? 'is-visible' : ''}`}
        onClick={() => scrollBy(-1)} aria-label="Categorías anteriores" tabIndex={-1}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="15 18 9 12 15 6" /></svg>
      </button>

      <div className="sp-catbar__tiles" ref={scrollerRef} onScroll={updateArrows} role="tablist" aria-label="Categorías">
        {categories.map((c, i) => {
          const isActive = c.key === active;
          return (
            <button
              key={c.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              className={`sp-ctile sp-focusable ${isActive ? 'is-active' : ''}`}
              onClick={(e) => select(c.key, e)}
              onKeyDown={(e) => onKeyNav(e, i)}
            >
              {/* <span className="sp-ctile__ic">
                {c.icon
                  ? c.icon
                  : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         dangerouslySetInnerHTML={{ __html: ICONS[c.key] || FALLBACK }} />}
              </span>  */}
              <span className="sp-ctile__name">{c.name}</span>
              {showCount && c.count != null && 
              <span className="sp-ctile__count">{c.count}</span>}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className={`sp-catbar__arrow sp-catbar__arrow--right ${canNext ? 'is-visible' : ''}`}
        onClick={() => scrollBy(1)} aria-label="Más categorías" tabIndex={-1}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="9 18 15 12 9 6" /></svg>
      </button>
    </div>
  );
}

export default CategoryBar;